import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, BookOpen, LogOut } from 'lucide-react';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/club', label: 'Writers Club' },
  { to: '/blog', label: "Writer's Blogck" },
  { to: '/faq', label: 'FAQ' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/affiliate', label: 'Partners' },
];

export default function GhostwriterLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('auth_token');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav data-testid="main-nav" className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-background/95 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
              <BookOpen className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="font-serif text-lg md:text-xl text-foreground tracking-tight">AI Ghostwriter <span className="text-primary">Pro</span></span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {NAV_LINKS.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`text-xs font-medium uppercase tracking-[0.2em] transition-colors ${location.pathname === link.to ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              {isLoggedIn ? (
                <>
                  <Link to="/ghostwriter/dashboard" data-testid="nav-dashboard-btn" className="text-xs font-semibold uppercase tracking-widest text-foreground hover:text-primary transition-colors">Dashboard</Link>
                  <button onClick={handleLogout} data-testid="nav-logout-btn" className="ml-3 text-muted-foreground hover:text-foreground"><LogOut className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <Link to="/get-started" data-testid="nav-cta-btn" className="rounded-sm px-5 py-2.5 bg-primary text-primary-foreground text-xs font-semibold uppercase tracking-widest hover:bg-white hover:text-black transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:shadow-[0_0_25px_rgba(255,255,255,0.5)]">
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-foreground" data-testid="mobile-menu-btn">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-white/5 px-6 pb-6" data-testid="mobile-nav">
            {NAV_LINKS.map(link => (
              <Link key={link.to} to={link.to} className="block py-3 text-sm text-muted-foreground hover:text-foreground transition-colors">{link.label}</Link>
            ))}
            <div className="mt-4 flex flex-col gap-2">
              {isLoggedIn ? (
                <>
                  <Link to="/ghostwriter/dashboard" className="text-sm text-primary font-semibold">Dashboard</Link>
                  <button onClick={handleLogout} className="text-sm text-muted-foreground">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/get-started" className="rounded-sm px-5 py-2.5 bg-primary text-primary-foreground text-sm font-semibold text-center">Get Started</Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 md:py-16 lg:py-24" data-testid="main-footer">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <span className="font-serif text-lg text-foreground">AI Ghostwriter <span className="text-primary">Pro</span></span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">Premium AI-powered ghostwriting service. From first draft to published book.</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4">Services</h4>
              <div className="flex flex-col gap-2">
                <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
                <Link to="/how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</Link>
                <Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</Link>
                <Link to="/club" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Writers Club</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4">Company</h4>
              <div className="flex flex-col gap-2">
                <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About</Link>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
                <Link to="/affiliate" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Partner Program</Link>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4">Get Started</h4>
              <div className="flex flex-col gap-2">
                <Link to="/get-started" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Start Free Trial</Link>
                <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Writer's Blogck</Link>
              </div>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground">Powered by <a href="https://web-techservices.com" className="text-primary hover:underline" target="_blank" rel="noreferrer">Web-TechServices.com AI</a></p>
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground transition-colors" data-testid="footer-admin-link">Admin</Link>
              <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} AI Ghostwriter Pro. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
